import { createRoot } from 'react-dom/client'
import { Component } from 'react'
import './index.css'
import App from './App.jsx'
import { ThemeProvider } from './context/ThemeContext'

// Auto-recover from stale-chunk errors after a redeploy. When Vercel
// ships new chunk hashes the browser's cached index.html still points
// at the old filenames; React.lazy() fails to fetch them with a "Failed
// to fetch dynamically imported module" / MIME-type error. Reload once
// to pick up the fresh index.html. A short sessionStorage cooldown
// prevents an infinite loop if the error has another cause while still
// allowing future deploys in the same browser session to recover.
const CHUNK_RELOAD_KEY = '__chunkReloadedAt';
const LEGACY_CHUNK_RELOAD_KEY = '__chunkReloadedOnce';
const CHUNK_RELOAD_COOLDOWN_MS = 15_000;
const looksLikeStaleChunk = (err) => {
  const msg = String(err?.message || err?.reason?.message || err?.reason || err || '');
  return /Failed to fetch dynamically imported module|Loading chunk \d+|ChunkLoadError|error loading dynamically imported module|module script.*MIME type|imported module.*404/i.test(msg);
};
const clearAppShellCaches = () => {
  const work = [];
  if ('serviceWorker' in navigator) {
    work.push(
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.allSettled(registrations.map((registration) => registration.unregister()))),
    );
  }
  if ('caches' in window) {
    work.push(
      window.caches.keys()
        .then((keys) => Promise.allSettled(keys.map((key) => window.caches.delete(key)))),
    );
  }
  return Promise.allSettled(work);
};
const reloadFreshAppShell = () => {
  const freshUrl = new URL(window.location.href);
  freshUrl.searchParams.set('_appRefresh', String(Date.now()));
  let navigated = false;
  const navigate = () => {
    if (navigated) return;
    navigated = true;
    window.location.replace(freshUrl.toString());
  };
  clearAppShellCaches().finally(navigate);
  window.setTimeout(navigate, 1200);
};
const consumeExplicitAppRefresh = () => {
  const url = new URL(window.location.href);
  const refreshValue = url.searchParams.get('_appRefresh');
  if (!refreshValue) return false;

  try {
    const handledKey = `__explicitAppRefresh:${refreshValue}`;
    if (sessionStorage.getItem(handledKey)) return false;
    sessionStorage.setItem(handledKey, '1');
  } catch { /* ignore quota / private mode */ }

  url.searchParams.delete('_appRefresh');
  let navigated = false;
  const navigate = () => {
    if (navigated) return;
    navigated = true;
    window.location.replace(url.toString());
  };
  clearAppShellCaches().finally(navigate);
  window.setTimeout(navigate, 1200);
  return true;
};
const maybeReloadForStaleChunk = (err) => {
  if (!looksLikeStaleChunk(err)) return false;
  try {
    sessionStorage.removeItem(LEGACY_CHUNK_RELOAD_KEY);
    const now = Date.now();
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (lastReloadAt && now - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch { /* ignore quota / private mode */ }
  reloadFreshAppShell();
  return true;
};
window.__recoverStaleChunk = maybeReloadForStaleChunk;
// Two more, for the case auto-recovery declines. The cooldown above
// exists to stop a reload loop when a chunk error has some other
// cause, but it also fires when recovery legitimately needs a second
// pass — unregistering the worker takes effect on the NEXT load — and
// the user lands on the error boundary instead. It needs to be able to
// tell that this is a stale build rather than a broken page, and its
// button has to clear the worker: a plain location.reload() goes
// straight back through the stale service worker that caused it, which
// is a button somebody can press forever without escaping.
window.__looksLikeStaleChunk = looksLikeStaleChunk;
window.__forceFreshAppShell = reloadFreshAppShell;
// Catch-all logging so transport-layer failures (fetch rejections from
// adblockers / VPNs / DLP agents) surface in the console even when the
// originating code path doesn't catch them. Runs AFTER the stale-chunk
// reload check so reloads still take priority.
// Sentry is not in this bundle, and this file never imports it. It is
// its own module script in index.html, loaded beside this one with no
// import between them — see src/lib/sentryBoot.js for why. Until it
// runs, the listeners below keep what they catch on a bounded queue it
// reads on start, so an early error is reported late rather than lost.
// Once it is up it leaves window.__sentryCapture for the boundary.
const EARLY_ERROR_LIMIT = 20;
const earlyErrors = [];
window.__earlyErrors = earlyErrors;
const sentryCapture = () => (typeof window.__sentryCapture === 'function' ? window.__sentryCapture : null);
const rememberEarlyError = (err) => {
  if (!err) return;
  const capture = sentryCapture();
  if (capture) { capture(err); return; }
  if (earlyErrors.length >= EARLY_ERROR_LIMIT) return;
  earlyErrors.push(err);
};

window.addEventListener('error', (e) => {
  if (maybeReloadForStaleChunk(e.error || e)) return;
  rememberEarlyError(e.error || e.message);
  console.error('[global] window error', e.error || e.message);
});
window.addEventListener('unhandledrejection', (e) => {
  if (maybeReloadForStaleChunk(e.reason)) return;
  rememberEarlyError(e.reason);
  console.error('[global] unhandledrejection', e.reason);
});
// A dynamic import that fails is usually CAUGHT by the code that made
// it — the marketing-package button catches it and shows "PDF generation
// returned no data" — so neither handler above ever sees it. Vite
// dispatches this event from its import helper before the error reaches
// the caller, whether or not the caller catches it. A 404 on a chunk
// after a deploy is a stale build, not a broken button, and the fix is
// the same fresh shell as above. preventDefault keeps Vite from also
// throwing into the caller while the reload is already under way.
window.addEventListener('vite:preloadError', (e) => {
  if (maybeReloadForStaleChunk(e.payload)) e.preventDefault();
});

// The last-resort net around the whole app. Sentry's own boundary did
// this, but importing it put the SDK in the boot bundle; this one hands
// what it catches to the SDK once that has arrived.
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    rememberEarlyError(error);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>Our team has been notified. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const renderApp = () => {
  createRoot(document.getElementById('root')).render(
    <ThemeProvider>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </ThemeProvider>,
  );
};

if (!consumeExplicitAppRefresh()) renderApp();
